import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type CSSProperties,
} from "react";
import {
  X,
  Minus,
  Plus,
  Maximize2,
  Fullscreen,
  LogOut,
  Pause,
  Play,
  Package,
  Sparkles,
  Hexagon,
  AlertCircle,
  CheckCircle2,
  Info,
  Settings as SettingsGear,
  Trash2,
  ExternalLink,
  Pin,
  Image as ImageIcon,
  EyeOff,
  RefreshCw,
  PanelTop,
  LayoutGrid,
} from "lucide-react";
import { osStore, type OsWindow } from "../os/store";
import {
  openWallpaperSettingsPanel,
  wallpaperHasSettings,
} from "../os/wallpaper-settings-panel";
import { dropTargets } from "../os/drop-targets";
import { decideDockDrag, expandRect, DOCK_HIT_PAD } from "../os/dock-drop.mjs";
import { resolveAppIcon } from "../os/icons";
import { AppHost } from "../os/AppHost";
import { contextMenuStore, type ContextMenuItem } from "../os/context-menu";
import { ContextMenuHost } from "./ContextMenuHost";
import {
  installGlobalTextEditing,
  isEditableTarget,
  hasTextSelection,
  openTextEditContextMenu,
  isBrowserEditHotkey,
} from "../os/text-editing";
import { startPresenceHeartbeat } from "../os/presence-host";
import { DialogHost } from "./DialogHost";
import { dialogStore } from "../os/dialog";
import { FileDialogHost } from "./FileDialogHost";
import { HomeGrid, addAppToHome } from "./HomeGrid";
import { DesktopGrid, openAddAppMenu, openAddWidgetMenu } from "./desktop/DesktopGrid";
import { SettingsApp } from "./SettingsApp";
import { TerminalApp } from "./terminal/TerminalApp";
import { deviceBus, type ShellMode } from "../os/device-host";
import { AppWindowMenuBar } from "./AppWindowMenuBar";
import { SpotlightSearch } from "./SpotlightSearch";
import { UpdateCenter } from "./UpdateCenter";
import { WidgetPanel } from "./WidgetPanel";
import { appMenuStore } from "../os/app-menu";
import { pkgUninstall } from "../os/api";
import { bindTooltipAttribute } from "../os/tooltip";
import {
  FOCUSABLE_SELECTOR,
  isFocusableCandidate,
  nextFocusIndex,
} from "../os/focus-cycle.mjs";

function useOs() {
  const [, setTick] = useState(0);
  useEffect(() => osStore.subscribe(() => setTick((t) => t + 1)), []);
  return osStore;
}

/** Tab stops inside `root`, in DOM order, skipping hidden/disabled/unrendered ones. */
function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((el) =>
    isFocusableCandidate(el)
  );
}

function WindowFrame({
  win,
  children,
  shellMode,
}: {
  win: OsWindow;
  children: ReactNode;
  shellMode: ShellMode;
}) {
  const store = useOs();
  const focused = store.focusId === win.id;
  const compact = shellMode === "phone" || shellMode === "tablet";
  const ref = useRef<HTMLDivElement>(null);

  /* ── Focus containment ─────────────────────────────────────────────────────
     In a real OS, Tab moves *within* the focused window; it never jumps to
     another app's icon. The browser's default is the opposite, so the shell
     contains it here, at the window frame.

     Contract (documented in docs/sdk/01-module-contract.md): an app that wants
     Tab for itself calls preventDefault() on its own keydown. Its handler sits
     deeper in the tree, so it runs first during bubbling and the OS then does
     nothing — that is what keeps Terminal's Tab completion working. */
  const onWindowKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    if (e.defaultPrevented) return; // the app claimed Tab — don't also move focus
    const el = ref.current;
    if (!el) return;
    // Always preventDefault, even with nothing to focus: otherwise a window
    // with no controls still leaks Tab out to the dock.
    e.preventDefault();
    const items = focusableWithin(el);
    const active = document.activeElement as HTMLElement | null;
    const next = nextFocusIndex(items.length, active ? items.indexOf(active) : -1, e.shiftKey);
    if (next === -1) return; // nothing focusable; focus stays where it is, contained
    items[next]?.focus({ preventScroll: true });
  };

  /* Containment only works if the keydown actually reaches this element. When
     DOM focus sits on document.body (user clicked the desktop, or the title bar
     — which preventDefaults its mousedown to drag), the event never gets here
     and the browser traverses globally. So pull focus into the window when it
     becomes the focused one.

     `force` is true for direct interaction with this window; the deferred check
     lets the app's own focus handling (e.g. Terminal's focusInput on mousedown)
     go first, so this is a fallback rather than an override. */
  const ensureFocusWithin = (force: boolean) => {
    const el = ref.current;
    if (!el || win.minimized) return;
    const active = document.activeElement as HTMLElement | null;
    if (active && el.contains(active)) return; // something inside already has it
    // Never yank focus out of a field the user is typing in, unless they just
    // interacted with this window themselves.
    if (!force && isEditableTarget(active)) return;
    const first = focusableWithin(el)[0];
    (first ?? el).focus({ preventScroll: true });
  };

  const wasFocused = useRef(false);
  useEffect(() => {
    const raised = focused && !win.minimized;
    const justRaised = raised && !wasFocused.current;
    wasFocused.current = raised;
    if (!justRaised) return;
    const id = requestAnimationFrame(() => ensureFocusWithin(false));
    return () => cancelAnimationFrame(id);
  });

  // Minimize hides the frame but keeps the React tree mounted so app state survives.
  // (return null unmounted AppHost and wiped Studio/Notepad/etc.)

  // Drag/resize move the DOM directly (GPU transform) and commit to the store
  // only on release. Updating the store every mousemove re-renders the whole
  // desktop — every window + app — 60×/sec, which (with backdrop-filter over the
  // live wallpaper) causes OS-wide flicker. The `.window--moving` class also
  // drops the backdrop blur so the compositor isn't recomputing it per frame.
  const onDrag = (e: MouseEvent) => {
    if ((e.target as HTMLElement).closest(".traffic")) return;
    if (win.maximized || shellMode === "phone") return;
    e.preventDefault();
    const el = ref.current;
    if (!el) return;
    store.focus(win.id);
    const sx = e.clientX;
    const sy = e.clientY;
    const ox = win.x;
    const oy = win.y;
    let nx = ox;
    let ny = oy;
    el.classList.add("window--moving");
    const move = (ev: globalThis.MouseEvent) => {
      nx = Math.max(0, ox + ev.clientX - sx);
      ny = Math.max(28, oy + ev.clientY - sy);
      el.style.transform = `translate(${nx - ox}px, ${ny - oy}px)`;
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      // Land at the final spot without a 1-frame flash before React re-renders.
      el.style.transform = "";
      el.style.left = nx + "px";
      el.style.top = ny + "px";
      el.classList.remove("window--moving");
      store.updateWindow(win.id, { x: nx, y: ny });
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  /**
   * Resize from any edge or corner. `dir` is a compass string ("n", "se", …);
   * dragging a west/north edge moves the origin as well as the size, and the
   * origin stops moving once the min size clamps so the window doesn't crawl.
   */
  const onResize = (dir: string) => (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const el = ref.current;
    if (!el) return;
    store.focus(win.id);
    const MIN_W = 320;
    const MIN_H = 200;
    const sx = e.clientX;
    const sy = e.clientY;
    const ox = win.x;
    const oy = win.y;
    const ow = win.w;
    const oh = win.h;
    let nx = ox;
    let ny = oy;
    let nw = ow;
    let nh = oh;
    el.classList.add("window--moving");
    const move = (ev: globalThis.MouseEvent) => {
      const dx = ev.clientX - sx;
      const dy = ev.clientY - sy;
      if (dir.indexOf("e") >= 0) nw = Math.max(MIN_W, ow + dx);
      if (dir.indexOf("s") >= 0) nh = Math.max(MIN_H, oh + dy);
      if (dir.indexOf("w") >= 0) {
        nw = Math.max(MIN_W, ow - dx);
        nx = ox + (ow - nw);
      }
      if (dir.indexOf("n") >= 0) {
        nh = Math.max(MIN_H, oh - dy);
        ny = oy + (oh - nh);
      }
      el.style.width = nw + "px";
      el.style.height = nh + "px";
      el.style.left = nx + "px";
      el.style.top = ny + "px";
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      el.classList.remove("window--moving");
      store.updateWindow(win.id, { x: nx, y: ny, w: nw, h: nh });
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const style: CSSProperties = (() => {
    const base: CSSProperties =
      shellMode === "phone" || win.maximized
        ? {
            left: 0,
            top: shellMode === "phone" ? 0 : 28,
            width: "100%",
            height:
              shellMode === "phone"
                ? "calc(100% - var(--os-mobile-nav-h, 64px))"
                : "calc(100% - 28px - 96px)",
            zIndex: win.z,
            borderRadius: shellMode === "phone" ? 0 : undefined,
          }
        : { left: win.x, top: win.y, width: win.w, height: win.h, zIndex: win.z };
    if (win.minimized) {
      return {
        ...base,
        // Keep layout/mount alive; park off-screen and inert
        opacity: 0,
        pointerEvents: "none",
        transform: "scale(0.94)",
        visibility: "hidden" as const,
        zIndex: 0,
      };
    }
    return base;
  })();

  const TitleIcon = resolveAppIcon(win.appId, win.title);

  const onWindowContext = (e: MouseEvent) => {
    if (isEditableTarget(e.target) || hasTextSelection()) {
      e.preventDefault();
      e.stopPropagation();
      openTextEditContextMenu(e.clientX, e.clientY, e.target);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    store.focus(win.id);
    const items: ContextMenuItem[] = [
      {
        id: "focus",
        label: "Bring to Front",
        icon: PanelTop,
        action: () => store.focus(win.id),
      },
      {
        id: "min",
        label: "Minimize",
        icon: Minus,
        action: () => store.updateWindow(win.id, { minimized: true }),
      },
      {
        id: "zoom",
        label: win.maximized ? "Restore" : "Zoom",
        icon: Maximize2,
        action: () => store.updateWindow(win.id, { maximized: !win.maximized }),
      },
      { id: "s1", separator: true },
      {
        id: "props",
        label: "Window Properties…",
        icon: Info,
        action: () =>
          store.notify(
            `${win.title} · ${win.appId}${win.version ? "@" + win.version : ""} · ${win.w}×${win.h}`,
            "info"
          ),
      },
      { id: "s2", separator: true },
      {
        id: "close",
        label: "Close",
        icon: X,
        danger: true,
        action: () => void store.requestCloseWindow(win.id),
      },
    ];
    contextMenuStore.open(e.clientX, e.clientY, {
      kind: "window",
      windowId: win.id,
      appId: win.appId,
      title: win.title,
    }, items);
  };

  return (
    <div
      ref={ref}
      className={`window ${focused ? "focused" : ""} ${win.minimized ? "is-minimized" : ""} ${shellMode === "phone" ? "is-phone" : ""} ${compact ? "is-compact" : ""}`}
      style={style}
      data-window-id={win.id}
      data-app-id={win.appId}
      data-app-version={win.version || ""}
      data-window-title={win.title}
      data-minimized={win.minimized ? "1" : "0"}
      tabIndex={-1}
      onMouseDown={() => {
        store.focus(win.id);
        // After the app's own mousedown handling; a no-op if it took focus.
        requestAnimationFrame(() => ensureFocusWithin(true));
      }}
      onKeyDown={onWindowKeyDown}
      onContextMenu={onWindowContext}
    >
      <div className="window-titlebar" onMouseDown={onDrag}>
        <div className="traffic">
          <button
            type="button"
            className="close"
            aria-label="Close"
            title="Close"
            onClick={(e) => {
              e.stopPropagation();
              void store.requestCloseWindow(win.id);
            }}
          >
            <X size={8} strokeWidth={3} />
          </button>
          <button
            type="button"
            className="min"
            aria-label="Minimize"
            title="Minimize"
            onClick={(e) => {
              e.stopPropagation();
              store.updateWindow(win.id, { minimized: true });
            }}
          >
            <Minus size={8} strokeWidth={3} />
          </button>
          <button
            type="button"
            className="max"
            aria-label="Zoom"
            title="Zoom"
            onClick={(e) => {
              e.stopPropagation();
              store.updateWindow(win.id, { maximized: !win.maximized });
            }}
          >
            {win.maximized ? (
              <Plus size={7} strokeWidth={3} />
            ) : (
              <Maximize2 size={7} strokeWidth={3} />
            )}
          </button>
        </div>
        <div className="window-title">
          <TitleIcon className="title-icon" size={14} strokeWidth={2} />
          <span>{win.title}</span>
        </div>
      </div>
      <AppWindowMenuBar windowId={win.id} />
      <div className="window-body">{children}</div>
      {!win.maximized &&
        ["n", "s", "e", "w", "ne", "nw", "se", "sw"].map((dir) => (
          <div
            key={dir}
            className={"window-grip window-grip--" + dir}
            onMouseDown={onResize(dir)}
          />
        ))}
    </div>
  );
}


function HelloApp() {
  const [wasm, setWasm] = useState("checking wasm…");
  useEffect(() => {
    (async () => {
      const { createModuleContext } = await import("../os/api");
      const ctx = createModuleContext();
      const inst = await ctx.wasm.instantiate("app.hello-demo");
      if (!inst) {
        setWasm("WASM not bundled for this build (host ready)");
        return;
      }
      const exp = inst.instance.exports as { add?: (a: number, b: number) => number };
      setWasm(exp.add ? `wasm add(2,3)=${exp.add(2, 3)}` : "wasm loaded");
    })();
  }, []);
  return (
    <div className="hello-app">
      <div>
        <div className="hero-icon">
          <Sparkles size={28} strokeWidth={1.75} />
        </div>
        <div
          style={{
            fontSize: 28,
            fontWeight: 650,
            letterSpacing: "-0.03em",
            marginBottom: 8,
          }}
        >
          Hello, ArcLog OS
        </div>
        <div style={{ opacity: 0.7, fontSize: 13 }}>
          Installable demo module · remove with pkg uninstall
        </div>
        <div
          style={{
            marginTop: 14,
            fontFamily: "var(--os-font-mono)",
            fontSize: 12,
            opacity: 0.8,
          }}
        >
          {wasm}
        </div>
      </div>
    </div>
  );
}

function AppBody({
  appId,
  version,
  windowId,
}: {
  appId: string;
  version?: string;
  windowId: string;
}) {
  // Prefer SDK modules for ALL apps. Terminal (IME) is the only temporary React builtin.
  // Everything else: AppHost dynamic ESM via /modules/:id[@version]/index.js
  const builtins = {
    // windowId so the shell's `exit` can close its own window (ctx.windows
    // .currentId is undefined without it).
    "app.terminal": () => <TerminalApp windowId={windowId} />,
    "app.settings": () => <SettingsApp windowId={windowId} />,
    "app.hello-demo": () => <HelloApp />,
  };
  return (
    <AppHost
      appId={appId}
      version={version}
      windowId={windowId}
      builtins={builtins}
    />
  );
}

function dockTint(appId: string): string {
  if (appId.includes("terminal")) return "tint-blue";
  if (appId.includes("settings")) return "tint-gray";
  if (appId.includes("hello") || appId.includes("demo")) return "tint-purple";
  if (appId.includes("note")) return "tint-orange";
  if (appId.includes("pack")) return "tint-teal";
  return "tint-teal";
}

function desktopContextItems(store: typeof osStore): ContextMenuItem[] {
  return [
    {
      id: "new-term",
      label: "Open Terminal",
      icon: resolveAppIcon("app.terminal"),
      action: () => store.openApp("app.terminal", "Terminal"),
    },
    {
      id: "settings",
      label: "System Settings…",
      icon: SettingsGear,
      action: () => store.openApp("app.settings", "Settings"),
    },
    { id: "s1", separator: true },
    {
      id: "wallpaper",
      label: store.wallpaperPaused ? "Resume Wallpaper" : "Pause Wallpaper",
      icon: ImageIcon,
      action: () => store.setWallpaperPaused(!store.wallpaperPaused),
    },
    {
      id: "change-wp",
      label: "Change Wallpaper…",
      icon: ImageIcon,
      action: () => {
        store.openApp("app.settings", "Settings");
      },
    },
    /* Always present, DISABLED when the active wallpaper declares no schema.
       The widget cog hides itself in the same situation, but a cog is attached
       to the widget it configures — you can see what it belongs to. This menu
       is global, so hiding the entry made a working feature look broken to
       anyone whose current wallpaper happened to have no settings. Greyed-out
       says "this exists, just not for this wallpaper". */
    {
      id: "wp-settings",
      label: wallpaperHasSettings(store.wallpaperId)
        ? "Wallpaper Settings…"
        : "Wallpaper Settings… (none for this one)",
      icon: "SlidersHorizontal",
      disabled: !wallpaperHasSettings(store.wallpaperId),
      action: () => openWallpaperSettingsPanel(),
    } as ContextMenuItem,
    { id: "s2", separator: true },
    {
      id: "fs",
      label: "Enter Full Screen",
      icon: Fullscreen,
      action: () => document.documentElement.requestFullscreen?.().catch(() => {}),
    },
    {
      id: "about",
      label: "About ArcLog OS",
      icon: Info,
      action: () => store.notify("ArcLog OS — canvas-first modular desktop", "info"),
    },
  ];
}

/** Below this a press on a dock icon is a launch, not a reorder. */
const PIN_DRAG_THRESHOLD = 4;
/** Caret x when the dock holds no pins yet — matches .dock's padding-left. */
const DOCK_EDGE_PAD = 10;
/** Half the gap between dock items, so the caret sits centred in the gap. */
const CARET_NUDGE = 3;

function dockAppContextItems(
  store: typeof osStore,
  appId: string,
  label: string
): ContextMenuItem[] {
  const pkg = store.packages.find((p) => p.manifest.id === appId || p.id === appId);
  const core = Boolean(pkg?.manifest.core);
  return [
    {
      id: "open",
      label: `Open ${label}`,
      icon: ExternalLink,
      action: () => store.openApp(appId),
    },
    {
      id: "pin",
      label: store.isPinned(appId) ? "Unpin from Dock" : "Pin to Dock",
      icon: Pin,
      action: () => {
        if (store.isPinned(appId)) store.unpinApp(appId);
        else store.pinApp(appId);
      },
    },
    {
      id: "home",
      label: "Add to Home screen",
      icon: LayoutGrid,
      action: () => {
        addAppToHome(appId);
        store.notify("Added to Home", "success");
      },
    },
    { id: "s1", separator: true },
    {
      id: "props",
      label: "App Properties…",
      icon: Info,
      action: () => {
        const vers = pkg?.versions
          ? Object.keys(pkg.versions).join(", ")
          : pkg?.manifest.version;
        store.notify(
          `${label} · ${appId} · default ${pkg?.defaultVersion || pkg?.manifest.version}${
            vers ? ` · installed: ${vers}` : ""
          }`,
          "info"
        );
      },
    },
    {
      id: "reload",
      label: "Relaunch",
      icon: RefreshCw,
      action: () => {
        store.closeAppWindows(appId);
        store.openApp(appId);
      },
    },
    { id: "s2", separator: true },
    {
      id: "uninstall",
      label: "Uninstall…",
      icon: Trash2,
      danger: true,
      disabled: core,
      action: async () => {
        try {
          await pkgUninstall(appId);
          store.unpinApp(appId);
          store.notify(`Uninstalled ${appId}`, "success");
        } catch (e) {
          store.notify(e instanceof Error ? e.message : String(e), "error");
        }
      },
    },
  ];
}

export function DesktopChrome() {
  const store = useOs();
  const [clock, setClock] = useState("");
  const [shellMode, setShellMode] = useState<ShellMode>(() => deviceBus.get().mode);
  const [homeOpen, setHomeOpen] = useState(false);

  // Drag-to-dock. The dock advertises itself as a drop target rather than the
  // desktop grid hunting for `.dock` in the DOM: the grid stays a grid, and the
  // "is it hovered / would it accept" affordance lives with the markup it
  // styles. rect() returns null whenever the dock isn't mounted (phone mode),
  // which the decision function reads as "no dock" → a plain grid move.
  const dockRef = useRef<HTMLDivElement | null>(null);

  /**
   * Viewport rects of the reorderable pins, left-to-right.
   *
   * Read from the DOM on demand rather than derived from store order: the dock
   * is the only thing that knows its own live geometry, and a drag has to
   * measure what is actually on screen at that instant.
   */
  const pinRects = () => {
    const el = dockRef.current;
    if (!el) return [];
    return Array.from(el.querySelectorAll<HTMLElement>(".dock-pin")).map((n) => {
      const r = n.getBoundingClientRect();
      return { left: r.left, right: r.right };
    });
  };

  const [dropHover, setDropHover] = useState(dropTargets.hover);
  useEffect(() => dropTargets.subscribe(() => setDropHover(dropTargets.hover)), []);
  useEffect(
    () =>
      dropTargets.register("dock", {
        rect: () => {
          const el = dockRef.current;
          if (!el) return null;
          const r = el.getBoundingClientRect();
          return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
        },
        // `.dock-pin`, not `.dock-item`: the trailing Applications button is a
        // .dock-item too, and measuring it let the caret reach a gap one past
        // the last pin — a position no reorder could honour.
        itemRects: () => pinRects(),
      }),
    []
  );
  const dockHover = dropHover?.targetId === "dock" ? dropHover : null;

  /* ── Reordering pins by dragging within the dock ──────────────────────
     Kept as local state rather than in the drop-target registry: this drag
     never leaves the dock's own markup, so nothing outside needs to see it.
     The registry stays the channel for drags that CROSS components. */
  const [pinDrag, setPinDrag] = useState<{
    appId: string;
    dx: number;
    /** Gap the caret is sitting in, 0..pins.length. */
    index: number;
    /** Pointer has left the dock: releasing here unpins. */
    off: boolean;
  } | null>(null);
  // A drag ends with a click event on the button it started on. Without this
  // flag every reorder would also launch the app it just moved.
  const pinDragMoved = useRef(false);

  const startPinDrag = (appId: string, e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    const startX = e.clientX;
    const startY = e.clientY;
    // Reset per press, not per drag: a drag released off the button fires no
    // click at all, and a flag left set would swallow the NEXT launch.
    pinDragMoved.current = false;

    const read = (ev: PointerEvent) =>
      decideDockDrag({
        point: { x: ev.clientX, y: ev.clientY },
        dockRect: expandRect(dropTargets.rect("dock"), DOCK_HIT_PAD),
        moved: true,
        itemRects: pinRects(),
      });

    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!pinDragMoved.current && Math.abs(dx) < PIN_DRAG_THRESHOLD && Math.abs(dy) < PIN_DRAG_THRESHOLD) {
        return;
      }
      pinDragMoved.current = true;
      const d = read(ev);
      setPinDrag({ appId, dx, index: d.index, off: d.action === "unpin" });
    };

    const onUp = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setPinDrag(null);
      if (!pinDragMoved.current) return; // a click; onClick launches the app
      const d = read(ev);
      if (d.action === "unpin") {
        const label = store.dockApps().find((a) => a.id === appId);
        store.unpinApp(appId);
        store.notify(`${label?.dock?.label || label?.name || appId} removed from the Dock`, "info");
        return;
      }
      // Silent on success: the dock visibly rearranging IS the confirmation,
      // and a toast per nudge during fiddling would be noise.
      store.reorderPin(appId, d.index);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // Caret x, in px from the dock's left edge. Measured from the live pin rects
  // rather than derived from item widths, so it stays correct whatever the dock
  // is scaled to. Positioned absolutely (see os.css) — an in-flow caret would
  // widen the dock and shift the very midpoints dockInsertIndex measures.
  const caretIndex = pinDrag && !pinDrag.off ? pinDrag.index : dockHover?.accepted ? dockHover.index : null;
  const [caretX, setCaretX] = useState(0);
  useLayoutEffect(() => {
    const el = dockRef.current;
    if (el == null || caretIndex == null) return;
    const base = el.getBoundingClientRect().left;
    const pins = Array.from(el.querySelectorAll<HTMLElement>(".dock-pin"));
    if (!pins.length) {
      setCaretX(DOCK_EDGE_PAD);
      return;
    }
    const i = Math.max(0, Math.min(pins.length, caretIndex));
    setCaretX(
      i === 0
        ? pins[0].getBoundingClientRect().left - base - CARET_NUDGE
        : pins[i - 1].getBoundingClientRect().right - base + CARET_NUDGE
    );
  }, [caretIndex]);

  useEffect(() => {
    const tick = () => {
      const d = new Date();
      const weekday = d.toLocaleDateString([], {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
      const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      setClock(`${weekday}  ${time}`);
    };
    tick();
    const id = setInterval(tick, 15_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    bindTooltipAttribute(document);
  });

  useEffect(() => deviceBus.subscribe((s) => setShellMode(s.mode)), []);

  useEffect(() => {
    const hasOpen = store.windows.some((w) => !w.minimized);
    if (shellMode === "phone") {
      setHomeOpen(!hasOpen);
    } else {
      setHomeOpen(false);
    }
  }, [store.windows, shellMode, store.focusId]);

  useEffect(() => {
    installGlobalTextEditing();
  }, []);

  useEffect(() => {
    return startPresenceHeartbeat(() => {
      const wins = store.windows;
      return {
        windowCount: wins.length,
        openApps: Array.from(new Set(wins.map((w) => w.appId))),
      };
    });
  }, [store]);

  useEffect(() => {
    const id = window.setInterval(() => store.persistSessionLayout(), 5000);
    const onHide = () => store.persistSessionLayout();
    window.addEventListener("pagehide", onHide);
    return () => {
      clearInterval(id);
      window.removeEventListener("pagehide", onHide);
    };
  }, [store]);

  // Global OS hotkeys — avoid macOS Cmd+Space clash
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isBrowserEditHotkey(e)) return;
      if (!(e.ctrlKey && e.altKey) || e.metaKey) return;
      const k = e.key.toLowerCase();
      if (k === "f" || e.code === "KeyF") {
        e.preventDefault();
        store.openApp("app.files", "Files");
      } else if (k === "a" || e.code === "KeyA") {
        e.preventDefault();
        sessionStorage.setItem("arclog.files.open", "/System/Applications");
        store.openApp("app.files", "Applications");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [store]);

  const enterFs = () => {
    document.documentElement.requestFullscreen?.().catch(() => {});
  };

  const focused = store.windows.find((w) => w.id === store.focusId && !w.minimized);
  const openIds = new Set(store.windows.map((w) => w.appId));

  return (
    <div className={"os-overlay mode-" + shellMode} data-shell-mode={shellMode}>
      {/* catches right-click on empty desktop (overlay itself is pointer-events: none) */}
      <div
        className="desktop-surface"
        onContextMenu={(e) => {
          e.preventDefault();
          const cx = e.clientX;
          const cy = e.clientY;
          contextMenuStore.open(
            cx,
            cy,
            { kind: "desktop" },
            [
              {
                id: "add-app",
                label: "Add app…",
                icon: "AppWindow",
                action: () => openAddAppMenu(),
              },
              {
                id: "add-widget",
                label: "Add widget…",
                icon: "LayoutGrid",
                action: () => openAddWidgetMenu(),
              },
              { id: "add-sep", separator: true },
              ...desktopContextItems(store),
            ]
          );
        }}
        onMouseDown={() => contextMenuStore.close()}
      />
      <HomeGrid visible={shellMode === "phone" ? homeOpen : shellMode !== "desktop"} />
      <DesktopGrid />
      <div
        className={"menubar" + (shellMode === "phone" ? " is-phone" : "")}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          contextMenuStore.open(e.clientX, e.clientY, { kind: "menubar" }, [
            {
              id: "about",
              label: "About ArcLog OS",
              icon: Info,
              action: () => store.notify("ArcLog OS · modular canvas desktop", "info"),
            },
            { id: "s1", separator: true },
            {
              id: "fs",
              label: "Toggle Full Screen",
              icon: Fullscreen,
              action: enterFs,
            },
          ]);
        }}
      >
        <div className="menubar-left">
          <div className="brand">
            <span className="brand-mark">
              <Hexagon size={12} strokeWidth={2.5} />
            </span>
            ArcLog OS
          </div>
          {focused && <span className="app-menu">{focused.title}</span>}
        </div>
        <div className="menubar-right">
                    <UpdateCenter />
          <button
            type="button"
            className="icon-btn"
            title="Reset session — close all apps and free memory (browser refresh keeps windows)"
            onClick={() => {
              void (async () => {
                const ok = await dialogStore.confirm({
                  title: "Reset session?",
                  message:
                    "Closes all apps and clears this tab session memory, then reloads.\n\n" +
                    "A normal browser refresh keeps your open windows and positions.\n" +
                    "Use reset after many apps or heavy guest use to free memory.",
                  okLabel: "Reset",
                  cancelLabel: "Cancel",
                  danger: true,
                });
                if (ok) (store as { rebootSession?: (o?: { reload?: boolean }) => void }).rebootSession?.({ reload: true });
              })();
            }}
          >
            <RefreshCw size={14} strokeWidth={2} />
          </button>
          <SpotlightSearch />
          <button
            type="button"
            className="icon-btn"
            title="Widgets"
            onClick={() => osStore.togglePanel()}
          >
            <LayoutGrid size={14} strokeWidth={2} />
          </button>
          <button type="button" className="icon-btn" onClick={enterFs} title="Full Screen">
            <Fullscreen size={14} strokeWidth={2} />
          </button>
          <span className="clock">{clock}</span>
        </div>
      </div>

      {store.windows.map((w) => (
        <WindowFrame key={w.id} win={w} shellMode={shellMode}>
          <AppBody appId={w.appId} version={w.version} windowId={w.id} />
        </WindowFrame>
      ))}

      {shellMode !== "phone" && (
        <div className="dock-wrap">
          <div
            ref={dockRef}
            className={
              "dock" +
              (dockHover ? (dockHover.accepted ? " os-dock-drop-ok" : " os-dock-drop-no") : "")
            }
          >
            {store.dockApps().map((app) => {
              const Icon = resolveAppIcon(app.id, app.dock?.label || app.name, app.dock?.icon);
              const open = openIds.has(app.id);
              const label = app.dock?.label || app.name;
              return (
                <Fragment key={app.id}>
                <button
                  type="button"
                  // .dock-pin marks the REORDERABLE items. pinRects() and the
                  // caret both key off it, so the fixed Applications button
                  // below can never be counted as a drop position.
                  className={
                    `dock-item dock-pin ${dockTint(app.id)} ${open ? "open" : ""}` +
                    (pinDrag?.appId === app.id
                      ? " is-pin-dragging" + (pinDrag.off ? " is-leaving" : "")
                      : "")
                  }
                  style={
                    pinDrag?.appId === app.id
                      ? ({ transform: `translateX(${pinDrag.dx}px)` } as CSSProperties)
                      : undefined
                  }
                  onPointerDown={(e: ReactPointerEvent) => startPinDrag(app.id, e)}
                  onClick={() => {
                    // Suppressed after a reorder: the click that closes a drag
                    // would otherwise launch the app that was just moved.
                    if (pinDragMoved.current) {
                      pinDragMoved.current = false;
                      return;
                    }
                    store.openApp(app.id);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    contextMenuStore.open(
                      e.clientX,
                      e.clientY,
                      { kind: "dock-app", appId: app.id, label },
                      dockAppContextItems(store, app.id, label)
                    );
                  }}
                >
                  <span className="dock-tooltip">{label}</span>
                  <span className="dock-glyph">
                    <Icon size={26} strokeWidth={1.75} />
                  </span>
                </button>
                </Fragment>
              );
            })}
            {/* Insertion point, drawn only for a drop the dock would ACCEPT —
                a caret under a refused widget would promise a landing spot the
                release is about to decline.

                It now FOLLOWS the pointer, because a mid-dock position is
                honoured: a new pin is appended and then moved to the caret's
                gap, and an existing pin is reordered to it. That is only safe
                because the caret is absolutely positioned (see os.css) — in
                flow it would widen the dock and shift the very midpoints
                dockInsertIndex measures, so it would oscillate between two
                gaps with the pointer held still. */}
            {caretIndex != null && (
              <span className="os-dock-drop-caret" style={{ left: caretX }} aria-hidden="true" />
            )}
            <button
              type="button"
              className="dock-item tint-gray"
              onClick={() => {
                store.openApp("app.packages", "Apps");
              }}
            >
              <span className="dock-tooltip">Apps</span>
              <span className="dock-glyph">
                <LayoutGrid size={26} strokeWidth={1.75} />
              </span>
            </button>
          </div>
        </div>
      )}

      {shellMode === "phone" && (
        <nav className="mobile-nav glass glass-strong" aria-label="Phone navigation">
          <button
            type="button"
            className={"mobile-nav-btn" + (homeOpen ? " on" : "")}
            onClick={() => {
              for (const w of store.windows) {
                if (!w.minimized) store.updateWindow(w.id, { minimized: true });
              }
              setHomeOpen(true);
            }}
          >
            Home
          </button>
          <button
            type="button"
            className="mobile-nav-btn"
            onClick={() => {
              setHomeOpen(false);
              store.openApp("app.packages", "Apps");
            }}
          >
            Apps
          </button>
          <button
            type="button"
            className="mobile-nav-btn"
            onClick={() => {
              setHomeOpen(false);
              store.openApp("app.settings", "Settings");
            }}
          >
            Settings
          </button>
        </nav>
      )}

      <div className="toasts" aria-live="polite">
        {store.toasts.map((t) => (
          <div
            key={t.id}
            className={`toast glass glass-strong toast-${t.kind}`}
            role="status"
          >
            <span className="toast-icon">
              {t.kind === "error" ? (
                <AlertCircle size={14} strokeWidth={2} />
              ) : t.kind === "success" ? (
                <CheckCircle2 size={14} strokeWidth={2} />
              ) : t.kind === "warn" ? (
                <AlertCircle size={14} strokeWidth={2} />
              ) : (
                <Info size={14} strokeWidth={2} />
              )}
            </span>
            <span className="toast-msg">{t.message}</span>
            <button
              type="button"
              className="toast-dismiss"
              aria-label="Dismiss"
              onClick={() => store.dismissToast(t.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <WidgetPanel />
      <ContextMenuHost />
      <DialogHost />
      <FileDialogHost />
    </div>
  );
}
